/**
 * The app's dashboard pieces, redrawn for the landing page's mockups.
 *
 * The real ones (StatusStrip, MonthTile, MatchBadge) read the language
 * context, and with it the Supabase client, which cannot load while the page
 * is prerendered in Node. These copy their markup and classes, so a mockup
 * looks like the app; the data is always invented.
 */
import { Check } from "lucide-react";
import { DotChip } from "@/components/shared/DotChip";
import { TopoTexture } from "@/components/TopoTexture";
import { cn } from "@/lib/utils";
import { type LandingLocale, pick } from "@/lib/landing/locale";
import { ROW_TINT, STATUSES, type LandingStatus } from "./statuses";

const BAR_ORDER: LandingStatus[] = ["matched", "invoice", "notNeeded", "missing", "unmatched"];

/** StatusStrip's segmented bar: one segment per status, sized by its count. */
export function StatusBar({
  counts,
  className,
}: {
  counts: Record<LandingStatus, number>;
  className?: string;
}) {
  const present = BAR_ORDER.filter((s) => counts[s] > 0);
  return (
    <div className={cn("flex h-2 gap-[3px]", className)} aria-hidden="true">
      {present.map((s) => (
        <div
          key={s}
          className="rounded-[3px]"
          style={{ flex: `${counts[s]} 1 0%`, backgroundColor: STATUSES[s].bar }}
        />
      ))}
    </div>
  );
}

/** MatchBadge: the status of one payment as a dot and a word. */
export function StatusChip({
  status,
  locale,
  className,
}: {
  status: LandingStatus;
  locale: LandingLocale;
  className?: string;
}) {
  return (
    <DotChip
      color={STATUSES[status].color}
      alert={status === "missing"}
      // Lighter red on dark cards, for contrast.
      className={cn(status === "missing" && "dark:text-red-400", className)}
    >
      {pick(STATUSES[status].label, locale)}
    </DotChip>
  );
}

export interface MockTx {
  date: string;
  text: { cs: string; en: string };
  /** Signed, already formatted: "−1 249,00 Kč". */
  amount: string;
  status: LandingStatus;
  /** The document it is matched with, if any. */
  doc?: string;
}

/** A row of the month's transactions table. */
export function TxRow({
  tx,
  locale,
  tinted = false,
  className,
}: {
  tx: MockTx;
  locale: LandingLocale;
  tinted?: boolean;
  className?: string;
}) {
  const incoming = !tx.amount.startsWith("−");
  return (
    <div
      className={cn(
        "grid grid-cols-[3.25rem_minmax(0,1fr)_auto] items-center gap-x-3 px-3 py-2.5 text-[13px]",
        tinted && ROW_TINT[tx.status],
        className
      )}
    >
      <span className="tabular-nums text-muted-foreground">{tx.date}</span>
      <span className="min-w-0">
        <span className="block break-words font-medium">{pick(tx.text, locale)}</span>
        <span className="mt-0.5 flex min-w-0 items-center gap-2">
          <StatusChip status={tx.status} locale={locale} />
          {tx.doc && (
            <span className="hidden break-words text-xs text-muted-foreground sm:inline">
              {tx.doc}
            </span>
          )}
        </span>
      </span>
      <span
        className={cn(
          "whitespace-nowrap text-right font-mono tabular-nums",
          incoming && "text-emerald-600 dark:text-emerald-400"
        )}
      >
        {tx.amount}
      </span>
    </div>
  );
}

/** One month of the year overview (MonthTile). */
export function MonthTileMock({
  name,
  detail,
  percent,
  net,
  counts,
  current = false,
  locale,
}: {
  name: string;
  detail: string;
  /** "95 %"; null when the month is complete (a check instead). */
  percent: string | null;
  net: string;
  counts: Record<LandingStatus, number>;
  current?: boolean;
  locale: LandingLocale;
}) {
  return (
    <div
      className={cn(
        "relative flex flex-col gap-3 overflow-hidden rounded-xl border p-3.5",
        current
          ? "border-primary/60 bg-primary/[0.06] shadow-[0_0_0_1px_hsl(var(--primary)/0.2),0_8px_24px_-12px_hsl(var(--primary)/0.35)] dark:bg-card"
          : "bg-card"
      )}
    >
      {current && (
        // The current month's texture, as MonthTile draws it.
        <TopoTexture
          className="inset-0 text-[hsl(var(--action))] opacity-[0.26] dark:text-primary dark:opacity-[0.16]"
          fade="none"
          size="760px"
          position="right top"
        />
      )}
      <div className="relative flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold tracking-tight">{name}</p>
            {current && (
              <span className="rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold text-[hsl(var(--action))] dark:text-primary">
                {locale === "cs" ? "Aktuální" : "Current"}
              </span>
            )}
          </div>
          <p className="mt-0.5 break-words text-xs tabular-nums text-muted-foreground">{detail}</p>
        </div>
        {percent === null ? (
          <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[hsl(var(--action))] dark:text-primary">
            <Check className="size-3" strokeWidth={2.5} />
          </span>
        ) : (
          <span className="text-xs font-semibold tabular-nums">{percent}</span>
        )}
      </div>
      <StatusBar counts={counts} className="relative h-1.5" />
      <div className="relative flex items-baseline justify-between gap-2 text-xs">
        <span className="text-muted-foreground">{locale === "cs" ? "Saldo" : "Net"}</span>
        <span className="font-semibold tabular-nums">{net}</span>
      </div>
    </div>
  );
}
